
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import FamilyService from '../../services/familyService';
import { useTutorial } from '../../tutorial/TutorialProvider';
import { useAuth } from '../../contexts/AuthContext';
import { logger } from '../../utils/logger';

export default function FamilySetupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [familyName, setFamilyName] = useState('');
  const [typingStarted, setTypingStarted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const familyService = FamilyService.getInstance();
  const { state: tutorialState, start } = useTutorial();
  const { user } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!familyName.trim()) {
      setError('Por favor, insira o nome da família');
      return;
    }

    setLoading(true);
    setError('');
    console.log('🚀 [FamilySetupPage] Criando família:', familyName);

    try {
      const family = await familyService.createFamily(familyName.trim());
      logger.sensitive('Família criada com sucesso', { familyId: family.id, name: family.name, created_by: family.created_by });
      
      // Salvar ID da família no localStorage para uso posterior
      localStorage.setItem('currentFamilyId', family.id!);
      
      navigate('/add-member');
    } catch (err: any) {
      console.error('❌ [FamilySetupPage] Erro ao criar família:', err);
      
      // Se for erro de autenticação, tentar redirecionar mesmo assim
      if (err.message?.includes('não autenticado')) {
        console.warn('⚠️ [FamilySetupPage] Erro de autenticação, mas tentando continuar...');
        // Aguardar um pouco e tentar novamente
        setTimeout(async () => {
          try {
            console.log('🔄 [FamilySetupPage] Tentando criar família novamente após delay...');
            const family = await familyService.createFamily(familyName.trim());
            console.log('✅ [FamilySetupPage] Família criada na segunda tentativa:', family);
            localStorage.setItem('currentFamilyId', family.id!);
            navigate('/add-member');
          } catch (retryError: any) {
            console.error('❌ [FamilySetupPage] Falhou na segunda tentativa:', retryError);
            setError('Não foi possível autenticar. Por favor, faça login novamente.');
            setLoading(false);
            // Redirecionar para login após 2 segundos
            setTimeout(() => navigate('/login'), 2000);
          }
        }, 3000);
      } else {
        setError(err.message || 'Erro ao criar família. Tente novamente.');
        setLoading(false);
      }
    }
  };

  // Auto-iniciar o tutorial nesta página, caso ainda não esteja ativo,
  // exceto quando o usuário tiver escolhido "Pular" recentemente
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const hasTutorial = params.has('tutorial');
      const suppressed = (() => {
        try {
          const key = user?.id ? `tutorial:${user.id}:suppressed` : 'tutorial:suppressed';
          const ns = localStorage.getItem(key) === '1';
          const legacy = localStorage.getItem('tutorial:suppressed') === '1';
          return ns || legacy;
        } catch { return false; }
      })();
      if (!tutorialState.active && !hasTutorial && !suppressed) {
        start('first_run');
      }
    } catch { /* ignore */ }
  }, [location.search, start, tutorialState.active, user?.id]);

  return (
    <div className="min-h-screen bg-gradient-to-t from-blue-200 to-white px-6 py-8">
      {/* Header */}
      <div className="text-center mb-8 pt-8">
        <div className="w-24 h-24 bg-white rounded-3xl flex items-center justify-center mb-4 mx-auto shadow-lg">
          <i className="ri-home-heart-fill text-4xl text-emerald-500"></i>
        </div>
        <h1 className="text-2xl font-bold text-gray-800 mb-2" style={{
          textShadow: '1px 2px 4px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.05)'
        }}>
          Configurar Família
        </h1>
        <p className="text-gray-600 text-sm">
          Vamos começar criando o seu núcleo familiar
        </p>
      </div>

      {/* Formulário */}
      <div className="bg-white rounded-3xl p-6 shadow-xl mb-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}
          
          <div className="space-y-6" data-tutorial-anchor="input-family-name">
            <Input
              label="Nome da Família"
              placeholder="Ex: Família Silva"
              value={familyName}
              onChange={(e) => {
                const v = e.target.value;
                setFamilyName(v);
                if (!typingStarted && v.trim().length > 0) {
                  // Dispara evento para o tutorial avançar para o spotlight do botão Continuar
                  try {
                    window.dispatchEvent(new CustomEvent('tutorial:family:name:started'));
                  } catch { /* ignore */ }
                  setTypingStarted(true);
                }
              }}
              required
            />

            <div className="bg-emerald-50 rounded-2xl p-4">
              <div className="flex items-start">
                <div className="w-8 h-8 bg-emerald-200 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <i className="ri-lightbulb-line text-emerald-600 text-sm"></i>
                </div>
                <div>
                  <p className="text-emerald-800 text-sm font-medium mb-1">Dica</p>
                  <p className="text-emerald-700 text-xs">
                    Este será o nome que aparecerá no dashboard principal. Você pode escolher algo carinhoso como "Família Santos" ou "Casa dos Oliveira".
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div data-tutorial-anchor="btn-continue-create-family">
            <Button type="submit" size="lg" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <i className="ri-loader-4-line mr-2 animate-spin"></i>
                  Criando...
                </>
              ) : (
                'Continuar'
              )}
            </Button>
          </div>
        </form>
      </div>

      {/* Progresso */}
      <div className="flex justify-center">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-2 bg-emerald-400 rounded-full"></div>
          <div className="w-2 h-2 bg-gray-200 rounded-full"></div>
          <div className="w-2 h-2 bg-gray-200 rounded-full"></div>
        </div>
      </div>
    </div>
  );
}
